import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, Field, TextInput } from "./adminShared";
import { formatDate } from "@/lib/format";
import { Trash2, Plus } from "lucide-react";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function Availability() {
  const [rules, setRules] = useState(null);
  const [blackouts, setBlackouts] = useState([]);
  const [bo, setBo] = useState({ startDate: "", endDate: "", reason: "" });

  const load = () => {
    api.get("/admin/availability-rules").then((r) => {
      const byDay = {};
      r.data.forEach((rule) => { byDay[rule.dayOfWeek] = rule; });
      const full = DAYS.map((_, d) => byDay[d] || { dayOfWeek: d, slots: [], isActive: false });
      setRules(full);
    }).catch(() => setRules(DAYS.map((_, d) => ({ dayOfWeek: d, slots: [], isActive: false }))));
    api.get("/admin/blackout-dates").then((r) => setBlackouts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleDay = (d) => setRules(rules.map((r) => r.dayOfWeek === d ? { ...r, isActive: !r.isActive, slots: r.isActive ? r.slots : (r.slots.length ? r.slots : [{ startTime: "10:00", endTime: "12:00" }]) } : r));
  const addSlot = (d) => setRules(rules.map((r) => r.dayOfWeek === d ? { ...r, slots: [...r.slots, { startTime: "10:00", endTime: "12:00" }] } : r));
  const updSlot = (d, i, key, val) => setRules(rules.map((r) => r.dayOfWeek === d ? { ...r, slots: r.slots.map((s, j) => j === i ? { ...s, [key]: val } : s) } : r));
  const rmSlot = (d, i) => setRules(rules.map((r) => r.dayOfWeek === d ? { ...r, slots: r.slots.filter((_, j) => j !== i) } : r));

  const saveRules = async () => {
    const active = rules.filter((r) => r.isActive && r.slots.length);
    try { await api.put("/admin/availability-rules", { rules: active }); toast.success("Availability saved"); load(); }
    catch { toast.error("Failed"); }
  };

  const addBlackout = async () => {
    if (!bo.startDate) { toast.error("Pick a date"); return; }
    await api.post("/admin/blackout-dates", { startDate: bo.startDate, endDate: bo.endDate || bo.startDate, reason: bo.reason, isFullDay: true });
    setBo({ startDate: "", endDate: "", reason: "" }); toast.success("Blackout added"); load();
  };
  const delBlackout = async (id) => { await api.delete(`/admin/blackout-dates/${id}`); load(); };

  if (!rules) return <p className="text-earth/50">Loading…</p>;

  return (
    <div>
      <h1 className="text-3xl text-earth mb-6" style={{ fontWeight: 600 }}>Availability</h1>
      <p className="text-earth/60 mb-6 text-sm">Set your weekly bookable slots (times in IST). Turn a day on, then add slots. Bookings honour a 24-hour minimum notice.</p>

      <Card className="p-6 mb-8">
        <div className="space-y-4">
          {rules.map((r) => (
            <div key={r.dayOfWeek} className="flex flex-col sm:flex-row sm:items-start gap-3 border-b hairline pb-4 last:border-0">
              <label className="flex items-center gap-2 w-40 shrink-0 cursor-pointer">
                <input type="checkbox" checked={r.isActive} onChange={() => toggleDay(r.dayOfWeek)} className="accent-[#C9A84C]" data-testid={`day-${r.dayOfWeek}`} />
                <span className={`font-semibold ${r.isActive ? "text-earth" : "text-earth/40"}`}>{DAYS[r.dayOfWeek]}</span>
              </label>
              {r.isActive && (
                <div className="flex-1 space-y-2">
                  {r.slots.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input type="time" value={s.startTime} onChange={(e) => updSlot(r.dayOfWeek, i, "startTime", e.target.value)} className="px-2 py-1.5 rounded-md bg-parchment border hairline text-earth text-sm" />
                      <span className="text-earth/40">–</span>
                      <input type="time" value={s.endTime} onChange={(e) => updSlot(r.dayOfWeek, i, "endTime", e.target.value)} className="px-2 py-1.5 rounded-md bg-parchment border hairline text-earth text-sm" />
                      <button onClick={() => rmSlot(r.dayOfWeek, i)} className="text-earth/40 hover:text-destructive"><Trash2 size={15} /></button>
                    </div>
                  ))}
                  <button onClick={() => addSlot(r.dayOfWeek)} className="text-sm font-semibold text-gold-dark flex items-center gap-1"><Plus size={14} /> Add slot</button>
                </div>
              )}
            </div>
          ))}
        </div>
        <button onClick={saveRules} className="btn-gold mt-6" data-testid="save-availability">Save weekly schedule</button>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl text-earth mb-4" style={{ fontWeight: 600 }}>Blackout dates</h2>
        <div className="grid sm:grid-cols-4 gap-3 items-end mb-4">
          <Field label="From"><TextInput type="date" value={bo.startDate} onChange={(e) => setBo({ ...bo, startDate: e.target.value })} /></Field>
          <Field label="To (optional)"><TextInput type="date" value={bo.endDate} onChange={(e) => setBo({ ...bo, endDate: e.target.value })} /></Field>
          <Field label="Reason"><TextInput value={bo.reason} onChange={(e) => setBo({ ...bo, reason: e.target.value })} placeholder="Travel, leave…" /></Field>
          <button onClick={addBlackout} className="btn-outline text-sm" data-testid="add-blackout">Add</button>
        </div>
        {blackouts.length === 0 ? <p className="text-earth/50 text-sm">No blackout dates.</p> : (
          <div className="divide-y divide-hairline">
            {blackouts.map((b) => (
              <div key={b.blackout_id} className="flex justify-between items-center py-2.5">
                <span className="text-earth/80 text-sm">{formatDate(b.startDate)}{b.endDate !== b.startDate && ` – ${formatDate(b.endDate)}`} {b.reason && `· ${b.reason}`}</span>
                <button onClick={() => delBlackout(b.blackout_id)} className="text-earth/40 hover:text-destructive"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
